import { Injectable, Injector } from '@angular/core';

import { SummaryService } from './summary.service';
import { TripsService } from './trips.service';
import { TicketsService } from './tickets.service';
import { BaseReportService } from './reportBase.service';
import type { IGetReportData, TReportOptions } from '@shared/models';
import { Constants } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats;

@Injectable()
export class ReportPassengerDetailService extends BaseReportService implements IGetReportData {
    private tripsService: TripsService;
    private ticketsService: TicketsService;
    private summaryService: SummaryService;

    constructor(injector: Injector) {
        super(injector);
        this.tripsService = injector.get(TripsService);
        this.ticketsService = injector.get(TicketsService);
        this.summaryService = injector.get(SummaryService);
    }

    public async getReportData(): Promise<any> {
        const allRows: any[] = [];
        const startingDate = this.options.startingDate;
        const endingDate = this.options.endingDate;

        if (!startingDate || !endingDate) {
            throw new Error('Starting date and ending date are required for passenger detail report');
        }

        const obj = await this.summaryService.getTripsByDateRange(startingDate, endingDate);
        const uids = obj.uids;

        let count = 0;

        for (const uid of uids) {
            const trip = await this.tripsService.getByUid(uid);
            const driver = trip.driver;
            const tickets = trip.tickets;

            for (const ticket of tickets) {
                const rowInfo = this.buildTicketRow(ticket, driver);
                allRows.push(rowInfo);
                ++count;
            }
        }

        // Add blank row
        this.addBlankRow(allRows);

        // Add summary row
        this.addSummaryTotalRow(allRows, count);

        // End of report data
        allRows.push(undefined);

        return { data: allRows };
    }

    private buildTicketRow(ticket: any, driver: string): any {
        const createdDate = this.formatDateTime(ticket.createdDate!);

        const assignedTime = this.formatTime(ticket.assignedDate!, DateTimeFormats.SmallTime);
        const assignedDur = this.dateService.formatDuration(ticket.createdDate!, ticket.assignedDate!);
        const assigned = `${assignedTime}(${assignedDur})`;

        const enrouteTime = this.formatTime(ticket.enrouteDate!, DateTimeFormats.SmallTime);
        const enrouteDur = this.dateService.formatDuration(ticket.assignedDate!, ticket.enrouteDate!);
        const enroute = `${enrouteTime}(${enrouteDur})`;

        const currentTime = this.formatTime(ticket.currentDate!, DateTimeFormats.SmallTime);
        const currentDur = this.dateService.formatDuration(ticket.enrouteDate!, ticket.currentDate!);
        const current = `${currentTime}(${currentDur})`;

        const completedTime = this.formatTime(ticket.completedDate!, DateTimeFormats.SmallTime);
        const completedDur = this.dateService.formatDuration(ticket.currentDate!, ticket.completedDate!);
        const completed = `${completedTime}(${completedDur})`;

        const passenger = ticket.passengerName;
        const destination = ticket.fromDealership 
            ? ticket.dropoff!.locationName 
            : ticket.pickup!.locationName;

        const reportColValues = [
            createdDate, 
            assigned, 
            enroute, 
            current, 
            completed, 
            passenger, 
            destination, 
            driver
        ];

        return this.buildRowInfo(reportColValues, 'data');
    }

    private addBlankRow(allRows: any[]): void {
        const reportColValues = [' ', ' ', ' '];
        const rowInfo = this.buildRowInfo(reportColValues, 'data');
        allRows.push(rowInfo);
    }

    private addSummaryTotalRow(allRows: any[], count: number): void {
        const reportColValues = ['Summary:', 'Total: ' + count.toString()];
        const rowInfo = this.buildRowInfo(reportColValues, 'summary');
        allRows.push(rowInfo);
    }
}
